/**
 * firestore.js — Firestore data access layer for M2B-Tools.
 */

import {
  collection,
  doc, 
  setDoc, 
  getDoc,
  getDocs, 
  deleteDoc, 
  serverTimestamp,
  writeBatch,
  updateDoc,
  deleteField,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  Timestamp,
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { uploadEventAudio } from '../events/eventAudio';
import {
  createGetUpcomingPublishedEvents,
  createGetUpcomingPublishedEventsPage,
  createGetUpcomingFeaturedPublishedEvents,
  createGetUpcomingPublishedEventsInRange,
  createGetEventAggregate,
  createGetUpcomingEvents,
  createGetUpcomingPublishedEventsAtLocation,
} from '../events/eventsApiIndex';
import { db, isFirestoreAvailable, storage } from '../../firebase';
import { convertLegacyDateToStandard, eventStartAtFromEvent, getEventCalendarDay } from './dateUtils';
import {
  editorialPickSortScore,
  isEditorialPick,
  isHomepageFeaturedEditorialPick,
  normalizeEditorialPicks,
  sortFeaturedEventsByDate,
} from '../events/eventEditorialPick';

const EVENTS_COLLECTION = 'v2-events';

function resolveEventsCollection(eventsCollection) {
  const name = typeof eventsCollection === 'string' ? eventsCollection.trim() : '';
  return name || EVENTS_COLLECTION;
}
const LOCATIONS_COLLECTION = 'v1-locations';
const ROUTES_COLLECTION = 'v1-routes';
const USERS_COLLECTION = 'users';
const PENDING_LOCATIONS_COLLECTION = 'v2-pending-locations';
const PARTNER_LOCATION_INDEX = 'partnerLocationIndex';

// Helper to upload image and get URL
const uploadImageAndGetURL = async (imageFile, eventId) => {
  if (!storage) {
    throw new Error('Firebase Storage not initialized.');
  }
  // File objects have a name; plain Blobs don't, so derive a safe filename.
  const extFromType = imageFile.type ? `.${imageFile.type.split('/')[1] || 'png'}` : '.png';
  const fileName = imageFile.name || `image-${Date.now()}${extFromType}`;
  const storageRef = ref(storage, `event-images/${eventId}/${fileName}`);
  await uploadBytes(storageRef, imageFile);
  return await getDownloadURL(storageRef);
};

// Helper to check Firestore availability
const checkFirestore = () => {
  if (!isFirestoreAvailable()) {
    console.error('Firebase not properly initialized. Check your configuration and internet connection.');
    return false;
  }
  return true;
};

export const getAllEvents = async () => {
  try {
    const eventsCollection = collection(db, EVENTS_COLLECTION);
    const snapshot = await getDocs(eventsCollection);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error("Error fetching all events: ", error);
    return [];
  }
};

/**
 * Normalize partner owners on an event doc.
 * Prefers `ownerUids`; falls back to scalar `ownerUid` for legacy docs.
 */
export const normalizeEventOwnerUids = (event) => {
  const fromArr = Array.isArray(event?.ownerUids)
    ? event.ownerUids.filter((u) => typeof u === 'string' && u.trim())
    : [];
  if (fromArr.length > 0) {
    return [...new Set(fromArr.map((u) => u.trim()))];
  }
  const single =
    typeof event?.ownerUid === 'string' && event.ownerUid.trim()
      ? event.ownerUid.trim()
      : null;
  return single ? [single] : [];
};

/** Write payload: `ownerUids` + synced primary `ownerUid` (first), or clear both. */
export const buildEventOwnerPayload = (ownerUids) => {
  const uids = [
    ...new Set(
      (ownerUids || [])
        .filter((u) => typeof u === 'string' && u.trim())
        .map((u) => u.trim())
    ),
  ];
  if (uids.length === 0) {
    return { ownerUids: [], ownerUid: null };
  }
  return { ownerUids: uids, ownerUid: uids[0] };
};

/** Partner Event Database: events owned by this uid (ownerUids or legacy ownerUid). */
export const getEventsByOwnerUid = async (ownerUid) => {
  if (!ownerUid || !checkFirestore()) return [];
  try {
    const eventsCollection = collection(db, EVENTS_COLLECTION);
    const [byArray, byScalar] = await Promise.all([
      getDocs(query(eventsCollection, where('ownerUids', 'array-contains', ownerUid))),
      getDocs(query(eventsCollection, where('ownerUid', '==', ownerUid))),
    ]);
    const byId = new Map();
    byArray.docs.forEach((d) => byId.set(d.id, { id: d.id, ...d.data() }));
    byScalar.docs.forEach((d) => {
      if (!byId.has(d.id)) byId.set(d.id, { id: d.id, ...d.data() });
    });
    return [...byId.values()];
  } catch (error) {
    console.error('Error fetching events by owner:', error);
    throw error;
  }
};

export const upsertPartnerLocationIndex = async (locationId, ownerUid) => {
  if (!locationId || !ownerUid || !checkFirestore()) {
    return { success: false, error: 'Missing locationId or ownerUid' };
  }
  try {
    await setDoc(
      doc(db, PARTNER_LOCATION_INDEX, String(locationId)),
      {
        ownerUid,
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );
    return { success: true };
  } catch (error) {
    console.error('Error writing partner location index:', error);
    return { success: false, error: error.message };
  }
};

export const deletePartnerLocationIndex = async (locationId) => {
  if (!locationId || !checkFirestore()) {
    return { success: false, error: 'Missing locationId' };
  }
  try {
    await deleteDoc(doc(db, PARTNER_LOCATION_INDEX, String(locationId)));
    return { success: true };
  } catch (error) {
    console.error('Error deleting partner location index:', error);
    return { success: false, error: error.message };
  }
};

/** Admin: locationId → ownerUid from partnerLocationIndex. */
export const getPartnerLocationOwnerMap = async () => {
  if (!checkFirestore()) return {};
  try {
    const snapshot = await getDocs(collection(db, PARTNER_LOCATION_INDEX));
    const map = {};
    snapshot.docs.forEach((d) => {
      const uid = d.data()?.ownerUid;
      if (uid) map[d.id] = uid;
    });
    return map;
  } catch (error) {
    console.error('Error loading partner location owner map:', error);
    return {};
  }
};

export const listPartnerLocationIds = async (ownerUid) => {
  if (!ownerUid || !checkFirestore()) return [];
  try {
    const q = query(
      collection(db, PARTNER_LOCATION_INDEX),
      where('ownerUid', '==', ownerUid)
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map((d) => d.id);
  } catch (error) {
    console.error('Error listing partner location ids:', error);
    return [];
  }
};

export const getAllLocations = async () => {
  try {
    const locationsCollection = collection(db, LOCATIONS_COLLECTION);
    const snapshot = await getDocs(locationsCollection);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error("Error fetching all locations: ", error);
    return [];
  }
};

// Delete an event from Firestore
export const deleteEvent = async (eventId, eventsCollection) => {
  try {
    if (!checkFirestore()) {
      return { 
        success: false, 
        error: 'Firebase not properly initialized.' 
      };
    }
    await deleteDoc(doc(db, resolveEventsCollection(eventsCollection), eventId));
    return { success: true };
  } catch (error) {
    console.error('Error deleting event:', error);
    return { success: false, error: error.message };
  }
};

// Delete a location from Firestore
export const deleteLocation = async (locationId) => {
  try {
    if (!checkFirestore()) {
      return { 
        success: false, 
        error: 'Firebase not properly initialized.' 
      };
    }
    await deleteDoc(doc(db, LOCATIONS_COLLECTION, locationId));
    return { success: true };
  } catch (error) {
    console.error('Error deleting location:', error);
    return { success: false, error: error.message };
  }
};

// Update a single field of an event
export const updateEventField = async (eventId, fieldName, fieldValue, eventsCollection) => {
  try {
    if (!checkFirestore()) {
      return { 
        success: false, 
        error: 'Firebase not properly initialized. Check your configuration and internet connection.' 
      };
    }

    const updateData = {};
    const eventRef = doc(db, resolveEventsCollection(eventsCollection), eventId);

    if (fieldName === 'imageFile' && fieldValue instanceof Blob) {
      // Accept both File and Blob objects (File extends Blob). The uploaded
      // image is stored and only its resulting URL is saved to Firestore -
      // never the raw Blob, which Firestore cannot serialize.
      const imageUrl = await uploadImageAndGetURL(fieldValue, eventId);
      updateData['imageUrl'] = imageUrl;
    } else if (fieldName === 'audioFile' && fieldValue instanceof Blob) {
      const audio = await uploadEventAudio(fieldValue, eventId);
      updateData.audioUrl = audio.audioUrl;
      updateData.audioPath = audio.audioPath;
      updateData.audioContentType = audio.audioContentType;
      updateData.audioTitle = audio.audioTitle;
    } else if (
      fieldValue === null ||
      fieldValue === '' ||
      fieldValue === 'none' ||
      (Array.isArray(fieldValue) && fieldValue.length === 0)
    ) {
      updateData[fieldName] = deleteField();
      if (fieldName === 'editorialPick') {
        updateData.editorialPickRank = deleteField();
      }
    } else {
      updateData[fieldName] = fieldValue;
    }

    if (fieldName === 'dateStr') {
      const eventStartAt = eventStartAtFromEvent({ dateStr: fieldValue });
      if (eventStartAt) {
        updateData.eventStartAt = eventStartAt;
      }
    }

    if (fieldName === 'editorialPick' || fieldName === 'dateStr') {
      const snap = await getDoc(eventRef);
      const existing = snap.exists() ? snap.data() : {};
      const pickCleared =
        fieldName === 'editorialPick' &&
        (fieldValue === null ||
          fieldValue === '' ||
          fieldValue === 'none' ||
          (Array.isArray(fieldValue) && fieldValue.length === 0));
      const nextEvent = {
        ...existing,
        dateStr: fieldName === 'dateStr' ? fieldValue : existing.dateStr,
        editorialPick: pickCleared
          ? null
          : fieldName === 'editorialPick'
            ? fieldValue
            : existing.editorialPick,
      };
      applyNormalizedEditorialPick(nextEvent, updateData, { pickCleared });
      if (!pickCleared && isEditorialPick(nextEvent.editorialPick, nextEvent)) {
        updateData.eventProspectGroup = true;
      }
    }
    
    updateData.updatedAt = serverTimestamp();
    
    await updateDoc(eventRef, updateData);

    if (fieldName === 'audioFile') {
      return {
        success: true,
        audioUrl: updateData.audioUrl,
        audioPath: updateData.audioPath,
        audioContentType: updateData.audioContentType,
        audioTitle: updateData.audioTitle,
      };
    }
    if (fieldName === 'imageFile') {
      return { success: true, imageUrl: updateData.imageUrl };
    }
    
    return { success: true };
  } catch (error) {
    console.error('Error updating event field:', error);
    return { 
      success: false, 
      error: error.message || 'Failed to update event field.' 
    };
  }
};

/**
 * Atomically update multiple fields on an event document.
 * Empty string/null values remove their fields so linked pairs such as
 * location + locationId cannot be left half-updated.
 */
export const updateEventFields = async (eventId, fieldValues, eventsCollection) => {
  try {
    if (!checkFirestore()) {
      return {
        success: false,
        error: 'Firebase not properly initialized. Check your configuration and internet connection.'
      };
    }

    const collectionName = resolveEventsCollection(eventsCollection);
    const updateData = {};
    Object.entries(fieldValues || {}).forEach(([fieldName, fieldValue]) => {
      if (fieldValue === null || fieldValue === '' || fieldValue === 'none' || (Array.isArray(fieldValue) && fieldValue.length === 0)) {
        updateData[fieldName] = deleteField();
      } else if (fieldValue !== undefined) {
        updateData[fieldName] = fieldValue;
      }
    });

    if (Object.prototype.hasOwnProperty.call(fieldValues || {}, 'dateStr')) {
      const eventStartAt = eventStartAtFromEvent({ dateStr: fieldValues.dateStr });
      if (eventStartAt) updateData.eventStartAt = eventStartAt;
    }

    const touchesPick =
      Object.prototype.hasOwnProperty.call(fieldValues || {}, 'editorialPick') ||
      Object.prototype.hasOwnProperty.call(fieldValues || {}, 'dateStr');
    if (touchesPick) {
      const eventRef = doc(db, collectionName, eventId);
      const snap = await getDoc(eventRef);
      const existing = snap.exists() ? snap.data() : {};
      const pickCleared =
        Object.prototype.hasOwnProperty.call(fieldValues || {}, 'editorialPick') &&
        (fieldValues.editorialPick === null ||
          fieldValues.editorialPick === '' ||
          fieldValues.editorialPick === 'none' ||
          (Array.isArray(fieldValues.editorialPick) &&
            fieldValues.editorialPick.length === 0));
      const nextEvent = {
        ...existing,
        dateStr: Object.prototype.hasOwnProperty.call(fieldValues || {}, 'dateStr')
          ? fieldValues.dateStr
          : existing.dateStr,
        editorialPick: pickCleared
          ? null
          : Object.prototype.hasOwnProperty.call(fieldValues || {}, 'editorialPick')
            ? fieldValues.editorialPick
            : existing.editorialPick,
      };
      applyNormalizedEditorialPick(nextEvent, updateData, { pickCleared });
      if (!pickCleared && isEditorialPick(nextEvent.editorialPick, nextEvent)) {
        updateData.eventProspectGroup = true;
      }
    }

    updateData.updatedAt = serverTimestamp();
    await updateDoc(doc(db, collectionName, eventId), updateData);
    return { success: true };
  } catch (error) {
    console.error('Error updating event fields:', error);
    return {
      success: false,
      error: error.message || 'Failed to update event fields.'
    };
  }
};

const stripUndefinedFields = (obj) =>
  Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== undefined));

const isPartnerEventWrite = (event) =>
  event?.source === 'partner' && typeof event?.ownerUid === 'string' && Boolean(event.ownerUid.trim());

/**
 * Persist normalized editorialPick (including stored saveTheDate) and refresh rank.
 * @param {Record<string, unknown>} nextEvent
 * @param {Record<string, unknown>} updateData
 * @param {{ pickCleared?: boolean }} [opts]
 */
function applyNormalizedEditorialPick(nextEvent, updateData, { pickCleared = false } = {}) {
  if (pickCleared) {
    updateData.editorialPick = deleteField();
    updateData.editorialPickRank = deleteField();
    return;
  }
  const normalized = normalizeEditorialPicks(nextEvent.editorialPick, nextEvent);
  if (!normalized.length) {
    updateData.editorialPick = deleteField();
    updateData.editorialPickRank = deleteField();
    return;
  }
  updateData.editorialPick = normalized;
  const rank = editorialPickSortScore(normalized, nextEvent);
  if (rank > 0) {
    updateData.editorialPickRank = rank;
  } else {
    updateData.editorialPickRank = deleteField();
  }
}

/** @param {Record<string, unknown>} eventData @param {{ defaultPublished?: boolean }} [opts] */
const enrichEventForFirestore = (eventData, opts = {}) => {
  const enriched = { ...eventData };
  if (opts.defaultPublished && !enriched.status && !enriched.submittedBy) {
    enriched.status = 'published';
  }
  const eventStartAt = eventStartAtFromEvent(enriched);
  if (eventStartAt) {
    enriched.eventStartAt = eventStartAt;
  } else if (enriched.dateStr) {
    console.warn(
      'Could not parse eventStartAt for event:',
      enriched.title || enriched.id || 'unknown'
    );
  }
  const normalized = normalizeEditorialPicks(enriched.editorialPick, enriched);
  if (normalized.length) {
    enriched.editorialPick = normalized;
    const rank = editorialPickSortScore(normalized, enriched);
    if (rank > 0) {
      enriched.editorialPickRank = rank;
    } else {
      delete enriched.editorialPickRank;
    }
  } else {
    delete enriched.editorialPick;
    delete enriched.editorialPickRank;
  }
  if (isEditorialPick(enriched.editorialPick, enriched)) {
    enriched.eventProspectGroup = true;
  }
  if (Array.isArray(enriched.artistIds)) {
    const seen = new Set();
    enriched.artistIds = enriched.artistIds
      .map((id) => String(id || '').trim())
      .filter((id) => {
        if (!id || seen.has(id)) return false;
        seen.add(id);
        return true;
      });
  }
  if (Array.isArray(enriched.artistLineup)) {
    enriched.artistLineup = enriched.artistLineup
      .map((row) => {
        if (!row || typeof row !== 'object') return null;
        const id = String(row.id || '').trim();
        const name = String(row.name || '').trim();
        const slug = String(row.slug || '').trim();
        if (!id || !name || !slug) return null;
        return { id, name, slug };
      })
      .filter(Boolean);
  }
  return enriched;
};

export const invalidatePublishedEventsCache = () => {};

// Save a single event (create or update)
export const saveEvent = async (eventData) => {
  if (!checkFirestore()) {
    return { success: false, error: 'Firebase not properly initialized.' };
  }

  try {
    const eventToSave = { ...eventData };

    // Sanitize title for document ID
    const docId = eventToSave.title.replace(/\s+/g, '-').toLowerCase();
    const eventRef = doc(db, EVENTS_COLLECTION, docId);

    if (eventToSave.imagePath && eventToSave.imagePath.startsWith('data:')) {
      eventToSave.imageData = eventToSave.imagePath;
      delete eventToSave.imagePath;
    }

    const payload = stripUndefinedFields({
      ...enrichEventForFirestore(eventToSave, { defaultPublished: true }),
      updatedAt: serverTimestamp(),
    });
    if (isPartnerEventWrite(payload)) {
      await setDoc(eventRef, payload);
    } else {
      await setDoc(eventRef, payload, { merge: true });
    }
    return { success: true, id: docId };
  } catch (error) {
    console.error('Error saving event:', error);
    return { success: false, error: error.message };
  }
};

// Update a single field of a location
export const updateLocationField = async (locationId, fieldName, fieldValue) => {
  try {
    if (!checkFirestore()) {
      return { 
        success: false, 
        error: 'Firebase not properly initialized.' 
      };
    }

    const updateData = {
      [fieldName]: fieldValue,
      updatedAt: serverTimestamp()
    };
    
    const locationRef = doc(db, LOCATIONS_COLLECTION, locationId);
    await updateDoc(locationRef, updateData);
    
    return { success: true };
  } catch (error) {
    console.error('Error updating location field:', error);
    return { 
      success: false, 
      error: error.message || 'Failed to update location field.' 
    };
  }
};

export const saveMultipleEvents = async (events, eventsCollection) => {
  if (!checkFirestore()) {
    return {
      success: false,
      error: 'Firebase not properly initialized.'
    };
  }

  const collectionName = resolveEventsCollection(eventsCollection);
  const batch = writeBatch(db);
  events.forEach(event => {
    const eventRef = doc(db, collectionName, event.id);
    const payload = stripUndefinedFields({
      ...enrichEventForFirestore(event, { defaultPublished: true }),
      updatedAt: serverTimestamp(),
    });
    if (isPartnerEventWrite(event) || isPartnerEventWrite(payload)) {
      batch.set(eventRef, payload);
    } else {
      batch.set(eventRef, payload, { merge: true });
    }
  });

  try {
    await batch.commit();
    return { success: true };
  } catch (error) {
    console.error('Error saving multiple events:', error);
    return { success: false, error: error.message };
  }
};

// Save a new route to Firestore
export const saveRoute = async (routeData) => {
  try {
    if (!checkFirestore()) {
      return { 
        success: false, 
        error: 'Firebase not properly initialized.' 
      };
    }

    const routeToSave = {
      ...routeData,
      createdAt: serverTimestamp()
    };
    
    const routeRef = doc(collection(db, ROUTES_COLLECTION));
    await setDoc(routeRef, routeToSave);
    
    return { success: true, id: routeRef.id };
  } catch (error) {
    console.error('Error saving route:', error);
    return { 
      success: false, 
      error: error.message || 'Failed to save route.' 
    };
  }
};

export const migrateEventsToNewDateFormat = async () => {
  if (!checkFirestore()) {
    return { 
      success: false, 
      error: 'Firebase not properly initialized.' 
    };
  }

  try {
    const eventsCollection = collection(db, EVENTS_COLLECTION);
    const snapshot = await getDocs(eventsCollection);
    const events = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

    const batch = writeBatch(db);
    let migratedCount = 0;

    for (const event of events) {
      if (event.date && event.date.day && event.date.month) {
        const newDateStr = convertLegacyDateToStandard(event.date);
        if (newDateStr) {
          const eventRef = doc(db, EVENTS_COLLECTION, event.id);
          batch.update(eventRef, {
            dateStr: newDateStr,
            date: deleteField(),
            updatedAt: serverTimestamp()
          });
          migratedCount++;
        }
      }
    }

    await batch.commit();

    return {
      success: true,
      message: `Successfully migrated ${migratedCount} events to the new date format.`,
      totalEvents: events.length,
      migratedCount: migratedCount,
      errorCount: 0
    };
  } catch (error) {
    console.error('Error during date format migration:', error);
    return {
      success: false,
      error: error.message || 'Failed to migrate events.'
    };
  }
};

// --- Auth / User Role helpers ---

export const getUserRole = async (uid) => {
  try {
    const userDoc = await getDoc(doc(db, USERS_COLLECTION, uid));
    if (userDoc.exists()) {
      return userDoc.data().role || null;
    }
    return null;
  } catch (error) {
    console.error('Error fetching user role:', error);
    return null;
  }
};

// --- Public read helpers (excludes pending/rejected) ---

export const getPublishedEvents = async () => {
  try {
    const eventsCollection = collection(db, EVENTS_COLLECTION);
    const snapshot = await getDocs(eventsCollection);
    return snapshot.docs
      .map(d => ({ id: d.id, ...d.data() }))
      .filter(event => !event.status || event.status === 'published');
  } catch (error) {
    console.error('Error fetching published events:', error);
    return [];
  }
};

/** Fetch a single published event by Firestore document id. */
export const getPublishedEventById = async (eventId) => {
  try {
    if (!checkFirestore() || !eventId) return null;
    const snap = await getDoc(doc(db, EVENTS_COLLECTION, eventId));
    if (!snap.exists()) return null;
    const data = { id: snap.id, ...snap.data() };
    if (data.status && data.status !== 'published') return null;
    return data;
  } catch (error) {
    console.error('Error fetching published event by id:', error);
    return null;
  }
};

export const getUpcomingPublishedEvents = createGetUpcomingPublishedEvents({
  db,
  eventsCollection: EVENTS_COLLECTION,
  checkFirestore,
  getPublishedEvents,
  getEventCalendarDay,
  firestore: { collection, query, where, orderBy, limit, startAfter, getDocs, Timestamp },
});

export const getUpcomingPublishedEventsPage = createGetUpcomingPublishedEventsPage({
  db,
  eventsCollection: EVENTS_COLLECTION,
  checkFirestore,
  getPublishedEvents,
  getEventCalendarDay,
  firestore: { collection, query, where, orderBy, limit, startAfter, getDocs, Timestamp },
});

export const getUpcomingFeaturedPublishedEvents = createGetUpcomingFeaturedPublishedEvents({
  db,
  eventsCollection: EVENTS_COLLECTION,
  checkFirestore,
  getPublishedEvents,
  getEventCalendarDay,
  isHomepageFeaturedEditorialPick,
  sortFeaturedEventsByDate,
  firestore: { collection, query, where, orderBy, limit, getDocs, Timestamp },
});

export const getUpcomingPublishedEventsInRange = createGetUpcomingPublishedEventsInRange({
  db,
  eventsCollection: EVENTS_COLLECTION,
  checkFirestore,
  getPublishedEvents,
  getEventCalendarDay,
  firestore: { collection, query, where, orderBy, getDocs, Timestamp },
});

export const getEventAggregate = createGetEventAggregate({
  db,
  checkFirestore,
  firestore: { doc, getDoc },
});

export const getUpcomingEvents = createGetUpcomingEvents({
  db,
  eventsCollection: EVENTS_COLLECTION,
  checkFirestore,
  getAllEvents,
  getEventCalendarDay,
  firestore: { collection, query, where, orderBy, getDocs, Timestamp },
});

export const getUpcomingPublishedEventsAtLocation = createGetUpcomingPublishedEventsAtLocation({
  db,
  eventsCollection: EVENTS_COLLECTION,
  checkFirestore,
  getPublishedEvents,
  getEventCalendarDay,
  firestore: { collection, query, where, orderBy, limit, getDocs, Timestamp },
});

// --- Partner submission helpers ---

export const submitEventAsPending = async (eventData, uid, email) => {
  if (!checkFirestore()) {
    return { success: false, error: 'Firebase not properly initialized.' };
  }
  try {
    const docId = `pending-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    const eventRef = doc(db, EVENTS_COLLECTION, docId);
    await setDoc(eventRef, stripUndefinedFields({
      ...enrichEventForFirestore(eventData),
      status: 'pending',
      submittedBy: uid,
      submitterEmail: email,
      source: 'partner',
      createdAt: serverTimestamp(),
    }));
    return { success: true, id: docId };
  } catch (error) {
    console.error('Error submitting event:', error);
    return { success: false, error: error.message };
  }
};

export const submitLocationAsPending = async (locationData, uid, email) => {
  if (!checkFirestore()) {
    return { success: false, error: 'Firebase not properly initialized.' };
  }
  try {
    const docRef = doc(collection(db, PENDING_LOCATIONS_COLLECTION));
    await setDoc(docRef, {
      ...locationData,
      status: 'pending',
      submittedBy: uid,
      submitterEmail: email,
      createdAt: serverTimestamp(),
    });
    return { success: true, id: docRef.id };
  } catch (error) {
    console.error('Error submitting location:', error);
    return { success: false, error: error.message };
  }
};

// --- Admin approval helpers ---

export const getPendingEvents = async () => {
  try {
    const q = query(collection(db, EVENTS_COLLECTION), where('status', '==', 'pending'));
    const snapshot = await getDocs(q);
    return snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
  } catch (error) {
    console.error('Error fetching pending events:', error);
    return [];
  }
};

export const getPendingLocations = async () => {
  try {
    const q = query(collection(db, PENDING_LOCATIONS_COLLECTION), where('status', '==', 'pending'));
    const snapshot = await getDocs(q);
    return snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
  } catch (error) {
    console.error('Error fetching pending locations:', error);
    return [];
  }
};

export const approveEvent = async (eventId) => {
  try {
    const eventRef = doc(db, EVENTS_COLLECTION, eventId);
    const snap = await getDoc(eventRef);
    const data = snap.exists() ? snap.data() : {};
    const update = {
      status: 'published',
      approvedAt: serverTimestamp(),
    };
    const eventStartAt = eventStartAtFromEvent(data);
    if (eventStartAt) {
      update.eventStartAt = eventStartAt;
    }
    await updateDoc(eventRef, update);
    return { success: true };
  } catch (error) {
    console.error('Error approving event:', error);
    return { success: false, error: error.message };
  }
};

export const rejectEvent = async (eventId) => {
  try {
    await updateDoc(doc(db, EVENTS_COLLECTION, eventId), {
      status: 'rejected',
      rejectedAt: serverTimestamp(),
    });
    return { success: true };
  } catch (error) {
    console.error('Error rejecting event:', error);
    return { success: false, error: error.message };
  }
};

export const approvePendingLocation = async (locationId) => {
  try {
    await updateDoc(doc(db, PENDING_LOCATIONS_COLLECTION, locationId), {
      status: 'approved',
      approvedAt: serverTimestamp(),
    });
    return { success: true };
  } catch (error) {
    console.error('Error approving location:', error);
    return { success: false, error: error.message };
  }
};

export const rejectPendingLocation = async (locationId) => {
  try {
    await updateDoc(doc(db, PENDING_LOCATIONS_COLLECTION, locationId), {
      status: 'rejected',
      rejectedAt: serverTimestamp(),
    });
    return { success: true };
  } catch (error) {
    console.error('Error rejecting location:', error);
    return { success: false, error: error.message };
  }
};